// ==================== COUPON FIELD ====================
// Coupon entry shared by every payment screen (Select Plan, Upgrade, Lead Gen,
// Buy Accounts and the admin's Assign Plan). The screen owns the applied code and
// the quote; this field only edits the code and shows what the quote returned.

import type { KeyboardEvent } from 'react';
import { Tag } from 'lucide-react';
import { Input, Button } from '../ui';
import type { CouponSummary } from '../../services/subscription.service';

interface CouponFieldProps {
  value: string;
  onChange: (value: string) => void;
  onApply: (code: string) => void | Promise<void>;
  /** The code the current quote was requested with; the button then offers Clear code */
  appliedCode?: string;
  coupon?: CouponSummary | null;
  error?: string | null;
  isLoading?: boolean;
  disabled?: boolean;
}

/** e.g. "20% off the plan, first payment only" */
const describeCouponTerms = (coupon: Pick<CouponSummary, 'discountPercent' | 'discountScope' | 'discountDuration'>) =>
  `${Number(coupon.discountPercent)}% off ${
    coupon.discountScope === 'PLAN_ONLY' ? 'the plan' : 'the whole payment'
  }, ${coupon.discountDuration === 'FIRST_PAYMENT' ? 'first payment only' : 'every payment'}`;

export const CouponField = ({
  value,
  onChange,
  onApply,
  appliedCode,
  coupon,
  error,
  isLoading,
  disabled,
}: CouponFieldProps) => {
  const code = value.trim().toUpperCase();
  const canRemove = !!appliedCode && code === appliedCode;

  const handleClick = () => {
    if (canRemove) onChange('');
    void onApply(canRemove ? '' : code);
  };

  // Enter performs the same Apply/Clear action as the button.
  const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key !== 'Enter') return;
    event.preventDefault();
    if (code || appliedCode) handleClick();
  };

  return (
    <div className="space-y-2">
      <div className="flex items-end gap-2">
        <Input
          label="Coupon code"
          placeholder="Enter coupon code"
          maxLength={30}
          leftIcon={<Tag size={16} />}
          value={value}
          onChange={(event) => onChange(event.target.value.toUpperCase())}
          onKeyDown={handleKeyDown}
          disabled={disabled || isLoading}
        />
        <Button
          type="button"
          variant="secondary"
          onClick={handleClick}
          disabled={disabled || isLoading || (!code && !appliedCode)}
          isLoading={isLoading}
        >
          {canRemove ? 'Clear code' : 'Apply'}
        </Button>
      </div>
      {coupon && (
        <div className="text-xs text-(--color-soft-gray) space-y-0.5">
          <p>
            {coupon.isNew ? (
              <>
                <span className="text-white font-medium">{coupon.code}</span> applied:{' '}
              </>
            ) : (
              <>
                Your coupon <span className="text-white font-medium">{coupon.code}</span>:{' '}
              </>
            )}
            {describeCouponTerms(coupon)}
          </p>
          {!coupon.appliesToThisPayment && <p className="text-(--color-muted-gray)">No discount on this payment.</p>}
        </div>
      )}
      {error && (
        <p role="alert" className="text-xs text-(--color-error)">
          {error}
        </p>
      )}
    </div>
  );
};
